import React, { useState } from 'react';
import classNames from 'classnames';
import { LinkedRepozeBrandLogo } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

// Accordion wrapper for footer link blocks on mobile.
// Extracts heading from block markdown to show in the always-visible header.
// CSS controls mobile vs desktop behaviour.
const FooterAccordionBlock = ({ block, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Use explicit mobile accordion titles by index, falling back to
  // markdown heading or blockName if a block doesn't have an override.
  const content = block?.text?.content || '';
  const headingMatch = content.match(/^#{1,6}\s+(.+)$/m);
  const fallback = headingMatch ? headingMatch[1] : block.blockName || '';
  const heading = block._accordionTitle || fallback;

  const toggle = () => setIsOpen(prev => !prev);
  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className={classNames(css.footerAccordion, { [css.footerAccordionOpen]: isOpen })}>
      <div
        className={css.footerAccordionHeader}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <span className={css.footerAccordionTitle}>{heading}</span>
        <span className={css.footerAccordionIcon} aria-hidden="true" />
      </div>
      <div className={css.footerAccordionBody}>
        {children}
      </div>
    </div>
  );
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  // Filter blocks for app links - be more specific to avoid duplicates
  const appLinkBlocks = blocks.filter(block =>
    block.blockId === 'app-links' ||
    (block.blockName?.toLowerCase().includes('app') &&
     (block.blockName?.toLowerCase().includes('store') ||
      block.blockName?.toLowerCase().includes('download'))) ||
    block.blockType === 'appLinks'
  );

  // Filter blocks for terms and privacy links
  const termsAndPrivacyBlocks = blocks.filter(block =>
    block.blockId === 'terms-and-privacy' ||
    block.blockName?.toLowerCase().includes('terms') ||
    block.blockName?.toLowerCase().includes('privacy') ||
    block.blockType === 'termsAndPrivacy'
  );

  const otherBlocks = blocks.filter(block =>
    block.blockId !== 'app-links' &&
    !(block.blockName?.toLowerCase().includes('app') &&
      (block.blockName?.toLowerCase().includes('store') ||
       block.blockName?.toLowerCase().includes('download'))) &&
    block.blockType !== 'appLinks' &&
    block.blockId !== 'terms-and-privacy' &&
    !block.blockName?.toLowerCase().includes('terms') &&
    !block.blockName?.toLowerCase().includes('privacy') &&
    block.blockType !== 'termsAndPrivacy'
  );

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={classNames(css.content, getContentCss(numberOfColumns))}>
          <div>
            <LinkedRepozeBrandLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              showByline={false}
              linkToExternalSite={linkLogoToExternalSite}
            />
          </div>
          <div className={css.sloganMobile}>
            <Field data={slogan} className={css.slogan} />
          </div>
          <div className={css.detailsInfo}>
            <div className={css.sloganDesktop}>
              <Field data={slogan} className={css.slogan} />
            </div>
            {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            ) : null}
          </div>
          <div className={classNames(css.grid, getGridCss(numberOfColumns))}>
            {otherBlocks.map((block, index) => {
              const blockId = block.blockId || `${sectionId}-block-${index + 1}`;
              const accordionTitles = ['Explore', 'Learn', 'Support'];
              const blockWithTitle = {
                ...block,
                _accordionTitle: accordionTitles[index],
              };
              return (
                <FooterAccordionBlock key={`${blockId}_i${index}`} block={blockWithTitle}>
                  <BlockBuilder
                    blocks={[block]}
                    sectionId={sectionId}
                    options={options}
                  />
                </FooterAccordionBlock>
              );
            })}
          </div>
        </div>

        {/* Combined Terms & Privacy and App Links Section */}
        {(termsAndPrivacyBlocks.length > 0 || appLinkBlocks.length > 0) && (
          <div className={css.combinedLinksSection}>
            <div className={css.combinedLinksContent}>
              {/* Terms & Privacy Links - Left Side */}
              {termsAndPrivacyBlocks.length > 0 && (
                <div className={css.termsAndPrivacyLeft} id="terms-and-privacy">
                  <BlockBuilder blocks={termsAndPrivacyBlocks} sectionId={sectionId} options={options} />
                </div>
              )}

              {/* App Links and Copyright - Right Side */}
              <div className={css.rightSideContent}>
                <div className={css.copyrightRight}>
                  <Field
                    data={
                      copyright?.content
                        ? {
                            ...copyright,
                            content: copyright.content.replace(
                              /© \d{4}/,
                              `© ${new Date().getFullYear()}`
                            ),
                          }
                        : copyright
                    }
                    className={css.copyright}
                  />
                </div>
                {appLinkBlocks.length > 0 && (
                  <div className={css.appLinksRight}>
                    <BlockBuilder blocks={appLinkBlocks} sectionId={sectionId} options={options} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
